import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdDownload, MdEdit, MdShare, MdClose, MdContentCopy } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import axios from 'axios';
import { VIEW_PURCHASE_ORDER_API, SEND_WHATSAPP_LINK_API, GET_WHATSAPP_CREDS_API } from '../config/API';
import Loader from '../components/common/Loader';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { encryptToken } from '../utils/secureToken';
import './AddGenerateRequest.css'; // Reuse existing styles
import { VENDOR_PORTAL_URL } from '../config/API';
import { formatDate } from '../utils/dateUtils';

const ViewGenerateRequest = () => {
    const { id } = useParams();


    const navigate = useNavigate();
    const [poData, setPoData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showShareModal, setShowShareModal] = useState(false);

    // Whatsapp Share State
    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [selectedVendorsForWhatsapp, setSelectedVendorsForWhatsapp] = useState([]);
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [whatsappCreds, setWhatsappCreds] = useState({ authKey: '', appKey: '' });

    const handleCopyLink = async (vendorId) => {
        // Backend expects two separate encrypted tokens: /encrypted_requestId/encrypted_vendorId
        const encryptedRequestId = encryptToken(Number(id));
        const encryptedVendorId = encryptToken(Number(vendorId));
        const link = ` ${VENDOR_PORTAL_URL}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

        try {
            await navigator.clipboard.writeText(link);
            toast.success('Link Copied Successfully!', { position: "top-right" });
        } catch (err) {
            toast.error('Failed to copy link', { position: "top-right" });
        }
    };

    useEffect(() => {
        const fetchPoDetails = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.get(VIEW_PURCHASE_ORDER_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    setPoData(response.data.data);
                } else {
                    console.error('Failed to fetch Request details:', response.data.message);
                }
            } catch (error) {
                console.error('Error fetching Request details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPoDetails();
    }, [id]);

    const handleWhatsappCheckboxChange = (vendorId) => {
        setSelectedVendorsForWhatsapp(prev => {
            if (prev.includes(vendorId)) {
                return prev.filter(id => id !== vendorId);
            } else {
                return [...prev, vendorId];
            }
        });
    };

    const handleSendWhatsapp = async () => {
        if (selectedVendorsForWhatsapp.length === 0) {
            toast.warn('Please select at least one vendor');
            return;
        }

        if (!whatsappCreds.authKey || !whatsappCreds.appKey) {
            toast.error("Whatsapp credentials not found. Please configure them first in Whatsapp Auth section.");
            return;
        }

        setSendingWhatsapp(true);
        try {
            const token = localStorage.getItem('accessToken');
            const vendors = poData.RequestVendors || [];
            const uniqueVendors = [...new Map(vendors.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()];

            const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
            const vendorsToSend = selectedVendorsForWhatsapp;
            
            for (let i = 0; i < vendorsToSend.length; i++) {
                const vendorId = vendorsToSend[i];
                const vendorObj = uniqueVendors.find(v => v.Vendor?.id === vendorId);
                if (!vendorObj || !vendorObj.Vendor) continue;

                // --- Control Delay between consecutive calls ---
                if (i > 0) await sleep(2000); // 2 second delay between calls

                // Check multiple possible key variations for the phone number
                const vendorData = vendorObj.Vendor;
                const phone = vendorData.whatsappNumber ||
                    vendorData.whatsAppNumber ||
                    vendorData.whatsapp_number ||
                    vendorData.contactNumber ||
                    vendorData.mobileNumber || '';

                if (!phone) continue;

                const encryptedRequestId = encryptToken(Number(id));
                const encryptedVendorId = encryptToken(Number(vendorId));
                const finalLink = `${VENDOR_PORTAL_URL}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

                const payload = {
                    vendorId: vendorId, // Consistent with other pages
                    to: phone,
                    appKey: whatsappCreds.appKey,
                    authKey: whatsappCreds.authKey,
                    message: `please click on this link to update rate\n\n${finalLink}`,
                    link: finalLink
                };

                const response = await axios.post(SEND_WHATSAPP_LINK_API(id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // ✅ Check for internal failures in response data even if status is 200/true
                const resDataArr = response.data?.data;
                const internalError = Array.isArray(resDataArr) && resDataArr.length > 0 && resDataArr[0].error;

                if (internalError) {
                    console.error(`Internal WhatsApp failure for ${vendorObj?.Vendor?.vendorName || vendorId}:`, internalError);
                }
            }

            // ✅ TRIGGER ADMIN NOTIFICATIONS: Exactly once after all vendors are processed.
            if (vendorsToSend.length > 0) {
                try {
                    await axios.post(
                        SEND_WHATSAPP_LINK_API(id),
                        { message: "ignore_this_message" },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                } catch (waAdminError) {
                    console.error('Failed to send admin notification:', waAdminError);
                }
            }
            Swal.fire('Success', 'Whatsapp messages sent successfully', 'success');
            setShowWhatsappModal(false);
            setSelectedVendorsForWhatsapp([]);

        } catch (error) {
            console.error('Error sending whatsapp:', error);
            const errorMsg = error.response?.data?.error?.error || error.response?.data?.message || 'Failed to send some messages';
            Swal.fire('Error', errorMsg, 'error');
        } finally {
            setSendingWhatsapp(false);
        }
    };

    const openWhatsappModal = async () => {
        const vendorsList = poData.RequestVendors || poData.PurchaseOrderVendors || [];
        if (poData && vendorsList.length > 0) {
            const uniqueVendors = [...new Map(vendorsList.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()];
            setSelectedVendorsForWhatsapp(uniqueVendors.map(v => v.Vendor?.id || v.Vendor?._id));
        }

        try {
            const token = localStorage.getItem('accessToken');
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                const response = await axios.get(GET_WHATSAPP_CREDS_API, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.status && response.data.data) {
                    setWhatsappCreds({
                        authKey: response.data.data.authKey || '',
                        appKey: response.data.data.appKey || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching whatsapp credentials:', error);
        }

        setShowWhatsappModal(true);
    };



    if (!poData) {
        return (
            <div className="add-po-page">
                <div className="page-header">
                    <button onClick={() => navigate('/generate-requests')} className="back-btn">
                        <MdArrowBack />
                    </button>
                    <h2>Generate Request Not Found</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="add-po-page">
            <div className="page-header">
                    <button onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/generate-requests')} className="back-btn">
                        <MdArrowBack />
                    </button>
                <h2>Generate Request: #{poData.poNumber || poData.reqNumber || 'N/A'}</h2>
                <div className="view-po-header-actions">
                    <button
                        className="edit-toggle-btn"
                        onClick={() => navigate(`/generate-requests/edit/${id}`)}
                    >
                        <MdEdit size={16} /> Edit Mode
                    </button>
                    <button
                        className="action-btn btn-share"
                        onClick={() => setShowShareModal(true)}
                        title="Share with Vendors"
                    >
                        <MdShare size={20} />
                    </button>
                    <button
                        className="action-btn btn-share"
                        style={{ backgroundColor: '#25D366' }}
                        onClick={openWhatsappModal}
                        title="Share via Whatsapp"
                    >
                        <FaWhatsapp size={20} />
                    </button>
                    <button
                        className="action-btn btn-download"
                        onClick={() => Swal.fire('PDF', 'PDF generation functionality is not implemented as per request.', 'info')}
                    >
                        <MdDownload size={20} />
                    </button>
                </div>
            </div>

            <div className="project-form">
                <div className="form-section">
                    <h3>General Details</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Vendor Name</label>
                            <div className="view-value">
                                {(() => {
                                    const vendors = poData.RequestVendors || poData.PurchaseOrderVendors || [];
                                    return vendors.length
                                        ? [...new Map(vendors.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()].map(v => v.Vendor?.vendorName).join(', ')
                                        : 'N/A';
                                })()}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Project Name / Site Name</label>
                            <div className="view-value">{poData.ProjectMaster?.projectName || 'N/A'}</div>
                        </div>
                        <div className="form-group">
                            <label>Order By (Employee)</label>
                            <div className="view-value">
                                {poData.Employee ? `${poData.Employee.firstName} ${poData.Employee.lastName}` : 'N/A'}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Date</label>
                            <div className="view-value">{formatDate(poData.date)}</div>
                        </div>
                        <div className="form-group full-width">
                            <label>Purpose / Particular</label>
                            <div className="view-value">{poData.particular || 'N/A'}</div>
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <div className="view-value">
                                <span className={`status-badge status-${(poData.status || 'pending').toLowerCase()}`}>
                                    {poData.status || 'Pending'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Items List</h3>
                    <div className="items-table-container">
                        <table className="items-table">
                            <thead className="thead-po">
                                <tr>
                                    <th style={{ width: '50px' }}>SR No</th>
                                    <th>Product Name</th>
                                    <th style={{ width: '250px' }}>Product Description</th>
                                    <th style={{ width: '100px' }}>Unit</th>
                                    <th style={{ width: '100px' }}>Size</th>
                                    <th style={{ width: '100px' }}>Qty</th>
                                    <th style={{ width: '120px' }}>Rate</th>
                                    <th style={{ width: '120px' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const rawItems = poData.RequestItems || poData.PurchaseOrderItems || [];
                                    return rawItems.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                            <td>
                                                <div style={{ fontWeight: '500', color: '#1e293b' }}>
                                                    {item.Product?.productName || item.productDescription}
                                                </div>
                                            </td>
                                            <td style={{ width: '250px', minWidth: '250px', maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                    {item.productDescription}
                                                </div>
                                            </td>
                                            <td>{item.unit || item.Unit?.unitName || item.Product?.Unit?.unitName || '-'}</td>
                                            <td>{item.size || 'N/A'}</td>
                                            <td>{item.qty}</td>
                                            <td>₹{item.rate ? parseFloat(item.rate).toLocaleString() : '0'}</td>
                                            <td style={{ fontWeight: '600' }}>
                                                ₹{item.total ? parseFloat(item.total).toLocaleString() : '0'}
                                            </td>
                                        </tr>
                                    ))
                                })()}
                            </tbody>
                        </table>
                    </div>

                    <div className="mobile-items-view">
                        {(() => {
                            const rawItems = poData.RequestItems || poData.PurchaseOrderItems || [];
                            return rawItems.map((item, index) => (
                                <div key={index} className="mobile-item-card">
                                    <div className="mobile-item-header">
                                        <div className="mobile-item-title">
                                            <div className="sr-badge">0{index + 1}</div>
                                            <strong>{item.Product?.productName || item.productDescription}</strong>
                                        </div>
                                    </div>
                                    <div className="mobile-item-body">
                                        <div className="mobile-item-row full-width">
                                            <span className="m-label">Description</span>
                                            <span className="m-value">{item.productDescription || '-'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Qty</span>
                                            <span className="m-value">{item.qty} {item.unit || item.Unit?.unitName || item.Product?.Unit?.unitName || 'Nos'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Size</span>
                                            <span className="m-value">{item.size || 'N/A'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Rate</span>
                                            <span className="m-value">₹{item.rate ? parseFloat(item.rate).toLocaleString() : '0'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Total</span>
                                            <span className="m-value" style={{ color: '#4f46e5' }}>₹{item.total ? parseFloat(item.total).toLocaleString() : '0'}</span>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>

                    <div className="po-summary">
                        <div className="grand-total-card">
                            <div className="total-label">Grand Total</div>
                            <div className="total-amount">₹{poData.grandTotal ? parseFloat(poData.grandTotal).toLocaleString() : '0'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Share Vendor Links Modal */}
            {showShareModal && (
                <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
                    <div className="modal-content share-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Share with Vendors</h3>
                            <button className="close-btn" onClick={() => setShowShareModal(false)}>
                                <MdClose />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="share-instruction">
                                Select a vendor to generate and copy their unique portal link.
                            </p>
                            <div className="vendor-links-list">
                                {(() => {
                                    const vendors = poData.RequestVendors || poData.PurchaseOrderVendors || [];
                                    return vendors.length > 0 ? (
                                        [...new Map(vendors.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()].map((pv, index) => (
                                            <div key={index} className="vendor-link-item">
                                                <div className="vendor-info">
                                                    <span className="vendor-name">{pv.Vendor?.vendorName || 'Unknown Vendor'}</span>
                                                </div>
                                                <button
                                                    className="copy-link-btn"
                                                    onClick={() => handleCopyLink(pv.Vendor?.id || pv.Vendor?._id)}
                                                >
                                                    <MdContentCopy /> Copy Link
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="no-data">No vendors assigned to this request.</p>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Whatsapp Share Modal */}
            {showWhatsappModal && (
                <div className="modal-overlay" onClick={() => setShowWhatsappModal(false)}>
                    <div className="modal-content share-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Share on Whatsapp</h3>
                            <button className="close-btn" onClick={() => setShowWhatsappModal(false)}>
                                <MdClose />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="share-instruction">
                                Select vendors to send the link via Whatsapp.
                            </p>
                            <div className="vendor-links-list">
                                {(() => {
                                    const vendors = poData.RequestVendors || poData.PurchaseOrderVendors || [];
                                    return vendors.length > 0 ? (
                                        [...new Map(vendors.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()].map((pv, index) => (
                                            <div key={index} className="vendor-link-item">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedVendorsForWhatsapp.includes(pv.Vendor?.id || pv.Vendor?._id)}
                                                        onChange={() => handleWhatsappCheckboxChange(pv.Vendor?.id || pv.Vendor?._id)}
                                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                    />
                                                    <div className="vendor-info">
                                                        <span className="vendor-name">{pv.Vendor?.vendorName || 'Unknown Vendor'}</span>
                                                        <span className="vendor-email">
                                                            {pv.Vendor?.whatsappNumber ||
                                                                pv.Vendor?.whatsAppNumber ||
                                                                pv.Vendor?.whatsapp_number ||
                                                                pv.Vendor?.contactNumber ||
                                                                pv.Vendor?.mobileNumber ||
                                                                'No Number'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="no-data">No vendors found.</p>
                                    );
                                })()}
                            </div>
                            <div className="form-actions" style={{ marginTop: '1.5rem', justifyContent: 'flex-end', display: 'flex' }}>
                                <button
                                    className="save-btn"
                                    style={{ backgroundColor: '#25D366' }}
                                    onClick={handleSendWhatsapp}
                                    disabled={sendingWhatsapp}
                                >
                                    {sendingWhatsapp ? 'Sending...' : 'Send WhatsApp'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ViewGenerateRequest;
